import { forwardRef } from "react";

import { isChrome, isMobile, isSafari } from "react-device-detect";
import type { LightBoxAttachment } from "@/components/ui/lightbox/light-box-attachment";
import LightBoxItem from "@/components/ui/lightbox/light-box-item";
import LightBoxItemImage from "@/components/ui/lightbox/light-box-item-image";
import LightBoxItemPdf from "@/components/ui/lightbox/light-box-item-pdf";
import LightBoxItemVideo from "@/components/ui/lightbox/light-box-item-video";
import {
  isImage,
  isPdf,
  isVideo,
  resolveAttachmentFileExtension,
} from "@/libs/file-helper";
import { AttachmentType } from "@/types/openapi-enum";

type LightBoxContentProps<
  TAttachment extends LightBoxAttachment = LightBoxAttachment,
> = {
  attachment: TAttachment;
  open: boolean;
  onProgress?: (state: any) => void;
};

const LightBoxContent = forwardRef(
  ({ attachment, open, onProgress }: LightBoxContentProps, ref) => {
    const fileExtension = resolveAttachmentFileExtension({
      fileName: attachment.fileName,
      contentUri: attachment.contentUri,
      contentType: attachment.contentType,
    });

    if (isImage(fileExtension) || attachment.type === AttachmentType.Photo) {
      return <LightBoxItemImage item={attachment} />;
    }

    if (
      (["hevc"].includes(fileExtension) && isChrome && isMobile) || //hevc only supported on chrome mobile
      (["avi"].includes(fileExtension) && isSafari) || //avi only supported on safari
      (isVideo(fileExtension) && !["avi", "hevc"].includes(fileExtension)) ||
      attachment.type === AttachmentType.Video
    ) {
      return (
        <LightBoxItemVideo
          item={attachment}
          onProgress={onProgress}
          open={open}
        />
      );
    }

    if (isPdf(fileExtension)) {
      return <LightBoxItemPdf item={attachment} />;
    }

    return <LightBoxItem item={attachment} />;
  },
);

LightBoxContent.displayName = "LightBoxContent";

export default LightBoxContent;
